import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Target, BookOpen, CalendarPlus, ListChecks, Users, CheckCircle2, Search,
  Plus, Trash2, X, TrendingUp, AlertTriangle, Video, ChevronRight, Save, Sparkles, ExternalLink, Link2, Mail, Eye, Send,
} from "lucide-react";
import { PageHeader, StatCard, SectionCard, EmptyState } from "../components/ui/kaizen";

const today = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/Montreal" });

function CoverageBar({ pct }) {
  const color = pct >= 75 ? "#10b981" : pct >= 40 ? "#f59e0b" : "#D90429";
  return (
    <div className="w-full h-2.5 rounded-full bg-zinc-700 overflow-hidden" data-testid="coverage-bar">
      <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: color }} />
    </div>
  );
}

export default function CurriculumCoverageModule() {
  const [tab, setTab] = useState("dashboard");
  const [loading, setLoading] = useState(true);
  const [dash, setDash] = useState(null);
  const [groups, setGroups] = useState([]);
  const [detail, setDetail] = useState(null); // group coverage detail

  // Planification
  const [planGroupId, setPlanGroupId] = useState("");
  const [planDate, setPlanDate] = useState(today());
  const [planObjective, setPlanObjective] = useState("");
  const [techLib, setTechLib] = useState([]);
  const [techSearch, setTechSearch] = useState("");
  const [planSelected, setPlanSelected] = useState([]); // [{technique_id,name}]

  // Séances + clôture
  const [sessions, setSessions] = useState([]);
  const [closeTarget, setCloseTarget] = useState(null); // session being closed
  const [taughtMap, setTaughtMap] = useState({}); // technique_id -> {checked,reps,status}
  const [profiles, setProfiles] = useState([]);
  const [presentMap, setPresentMap] = useState({}); // profile_id -> bool

  // V2/V3
  const [alerts, setAlerts] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [reco, setReco] = useState(null);
  const [recoLoading, setRecoLoading] = useState(false);
  const [videoEng, setVideoEng] = useState(null);
  const [vidTech, setVidTech] = useState(null); // technique_id en cours de liaison vidéo
  const [vidSuggest, setVidSuggest] = useState(null); // {search_query, search_url, best_candidate, reason, candidates}
  const [vidLoading, setVidLoading] = useState(false);
  const [manualUrl, setManualUrl] = useState("");
  const [digestBusy, setDigestBusy] = useState(false);
  // Auto-liaison vidéos en lot (file de validation IA)
  const [vqDisc, setVqDisc] = useState("");
  const [vqDiscList, setVqDiscList] = useState([]);
  const [vqList, setVqList] = useState([]);
  const [vqIndex, setVqIndex] = useState(0);
  const [vqSuggest, setVqSuggest] = useState(null);
  const [vqLoading, setVqLoading] = useState(false);
  const [vqManual, setVqManual] = useState("");

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/ped/dashboard");
      setDash(data);
      setGroups(data.groups || []);
    } catch (e) {
      toast.error("Erreur de chargement du tableau de bord.");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSessions = useCallback(async () => {
    try {
      const { data } = await api.get("/ped/sessions");
      setSessions(data.sessions || []);
    } catch (e) { /* noop */ }
  }, []);

  useEffect(() => { loadDashboard(); loadSessions(); }, [loadDashboard, loadSessions]);

  const openGroupDetail = async (groupId) => {
    setReco(null); setVideoEng(null);
    try {
      const { data } = await api.get(`/ped/groups/${groupId}/coverage`);
      setDetail(data);
    } catch (e) { toast.error("Erreur chargement couverture du groupe."); }
    try {
      const { data } = await api.get(`/ped/groups/${groupId}/video-engagement`);
      setVideoEng(data);
    } catch (e) { /* noop */ }
  };

  const loadAlerts = useCallback(async () => {
    try { const { data } = await api.get("/ped/alerts"); setAlerts(data); }
    catch (e) { toast.error("Erreur chargement des alertes."); }
  }, []);

  const loadPrograms = useCallback(async () => {
    try { const { data } = await api.get("/ped/programs/coverage"); setPrograms(data.programs || []); }
    catch (e) { toast.error("Erreur chargement des programmes."); }
  }, []);

  const fetchReco = async (groupId) => {
    setRecoLoading(true); setReco(null);
    try {
      const { data } = await api.get(`/ped/groups/${groupId}/recommendations`);
      setReco(data.recommendation || "");
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec de la génération IA."); }
    finally { setRecoLoading(false); }
  };

  // ---- Liaison vidéo assistée par IA ----
  const openVideoSuggest = async (techId) => {
    setVidTech(techId); setVidSuggest(null); setManualUrl(""); setVidLoading(true);
    try {
      const { data } = await api.get(`/ped/techniques/${techId}/video-ai-suggest`);
      setVidSuggest(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec de la suggestion IA."); setVidTech(null); }
    finally { setVidLoading(false); }
  };

  const linkVideo = async (techId, url) => {
    if (!url) return;
    try {
      await api.put(`/ped/techniques/${techId}/video`, { video_url: url });
      toast.success("Vidéo liée à la technique.");
      setVidTech(null); setVidSuggest(null); setManualUrl("");
      if (detail?.group?.id) openGroupDetail(detail.group.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec de la liaison."); }
  };

  // ---- Digest pédagogique hebdo instructeurs ----
  const previewDigest = async () => {
    setDigestBusy(true);
    try {
      const { data } = await api.get("/ped/test-instructor-digest?ia=false");
      const w = window.open("", "_blank");
      if (w) { w.document.write(data.html); w.document.close(); }
      toast.success(`Aperçu généré (${data.instructors_count} instructeur·rice·s).`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec de l'aperçu."); }
    finally { setDigestBusy(false); }
  };
  const sendDigest = async () => {
    if (!window.confirm("Envoyer maintenant le digest pédagogique hebdo (courriel réel via le compte Google connecté) ?")) return;
    setDigestBusy(true);
    try {
      const { data } = await api.post("/ped/send-instructor-digest");
      if (data.ok) toast.success(`Digest envoyé à ${data.sent_to} (${data.instructors_count} instructeur·rice·s).`);
      else toast.error(data.reason || "Envoi impossible (Gmail non connecté ?).");
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec de l'envoi."); }
    finally { setDigestBusy(false); }
  };

  // ---- File d'auto-liaison vidéos (IA) ----
  const loadVqDisciplines = useCallback(async () => {
    try {
      const { data } = await api.get("/ped/disciplines");
      setVqDiscList(data.disciplines || []);
    } catch (e) { /* noop */ }
  }, []);

  const vqRunSuggest = async (list, idx) => {
    if (!list[idx]) { setVqSuggest(null); return; }
    setVqLoading(true); setVqSuggest(null); setVqManual("");
    try {
      const { data } = await api.get(`/ped/techniques/${list[idx].id}/video-ai-suggest`);
      setVqSuggest(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec suggestion IA (budget LLM ?)."); }
    finally { setVqLoading(false); }
  };

  const loadVideoQueue = async (slug) => {
    setVqDisc(slug); setVqList([]); setVqIndex(0); setVqSuggest(null);
    if (!slug) return;
    try {
      const { data } = await api.get(`/ped/techniques`, { params: { discipline: slug } });
      const missing = (data.techniques || []).filter((t) => !t.video_url);
      setVqList(missing);
      if (missing.length) vqRunSuggest(missing, 0);
    } catch (e) { toast.error("Erreur de chargement des techniques."); }
  };

  const vqAdvance = (newList) => {
    const list = newList || vqList;
    const next = vqIndex + (newList ? 0 : 1);
    setVqIndex(next);
    vqRunSuggest(list, next);
  };

  const vqLink = async (url) => {
    if (!url) return;
    const cur = vqList[vqIndex];
    try {
      await api.put(`/ped/techniques/${cur.id}/video`, { video_url: url });
      toast.success(`Vidéo liée à « ${cur.name} ».`);
      const remaining = vqList.filter((_, i) => i !== vqIndex);
      setVqList(remaining);
      vqAdvance(remaining);
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec de la liaison."); }
  };

  const vqSkip = () => vqAdvance();

  const [discReco, setDiscReco] = useState({}); // slug -> text
  const [discRecoLoading, setDiscRecoLoading] = useState(null); // slug
  const fetchDiscReco = async (slug) => {
    setDiscRecoLoading(slug);
    try {
      const { data } = await api.get(`/ped/disciplines/${slug}/recommendations`);
      setDiscReco((m) => ({ ...m, [slug]: data.recommendation || "" }));
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec de la génération IA."); }
    finally { setDiscRecoLoading(null); }
  };

  // ---- Planification ----
  const planGroup = groups.find((g) => g.id === planGroupId);
  const loadTechLib = useCallback(async (disciplineSlug, q) => {
    if (!disciplineSlug) { setTechLib([]); return; }
    try {
      const { data } = await api.get(`/ped/techniques`, { params: { discipline: disciplineSlug, q: q || undefined } });
      setTechLib(data.techniques || []);
    } catch (e) { setTechLib([]); }
  }, []);

  useEffect(() => {
    if (planGroup?.discipline_slug) loadTechLib(planGroup.discipline_slug, techSearch);
  }, [planGroup?.discipline_slug, techSearch, loadTechLib]);

  const togglePlanTech = (t) => {
    setPlanSelected((prev) =>
      prev.find((x) => x.technique_id === t.id)
        ? prev.filter((x) => x.technique_id !== t.id)
        : [...prev, { technique_id: t.id, name: t.name }]
    );
  };

  const savePlan = async () => {
    if (!planGroupId) return toast.error("Choisis un groupe.");
    if (planSelected.length === 0) return toast.error("Sélectionne au moins une technique.");
    try {
      await api.post("/ped/sessions", {
        group_id: planGroupId, date: planDate, objective: planObjective,
        planned_techniques: planSelected.map((p) => ({ technique_id: p.technique_id, name: p.name })),
      });
      toast.success("Séance planifiée ✅");
      setPlanSelected([]); setPlanObjective("");
      loadSessions();
      setTab("sessions");
    } catch (e) { toast.error("Erreur lors de la planification."); }
  };

  // ---- Clôture ----
  const startClose = async (session) => {
    setCloseTarget(session);
    const tm = {};
    (session.planned_techniques || []).forEach((p) => {
      tm[p.technique_id] = { checked: true, reps: p.target_reps || 0, status: "taught", name: p.name };
    });
    setTaughtMap(tm);
    setPresentMap({});
    try {
      const { data } = await api.get(`/ped/profiles`, { params: { discipline: session.discipline_slug } });
      setProfiles(data.profiles || []);
    } catch (e) { setProfiles([]); }
    // Pré-remplissage des présences depuis le module Présences (kiosque + saisie)
    try {
      const { data } = await api.get(`/ped/sessions/${session.id}/suggested-attendance`);
      if (data.profile_ids?.length) {
        const pm = {};
        data.profile_ids.forEach((id) => { pm[id] = true; });
        setPresentMap(pm);
        toast.success(`${data.count} présence(s) pré-remplie(s) depuis les Présences`);
      }
    } catch (e) { /* noop */ }
  };

  const submitClose = async () => {
    const taught = Object.entries(taughtMap)
      .filter(([, v]) => v.checked)
      .map(([tid, v]) => ({ technique_id: tid, name: v.name, status: v.status, reps: Number(v.reps) || 0, participants: v.excluded && Object.values(v.excluded).some(Boolean) ? profiles.filter((p) => presentMap[p.id] && !v.excluded[p.id]).map((p) => p.id) : null }));
    if (taught.length === 0) return toast.error("Coche au moins une technique enseignée.");
    const attendance = profiles
      .filter((p) => presentMap[p.id])
      .map((p) => ({ profile_id: p.id, name: p.student_name, status: "present" }));
    try {
      const { data } = await api.post(`/ped/sessions/${closeTarget.id}/close`, {
        taught_techniques: taught, attendance,
      });
      toast.success(`Séance clôturée — ${data.present_count} présent(s), ${data.taught_count} techniques · carnets mis à jour${data.skipped_count ? ` · ${data.skipped_count} attribution(s) retenue(s) (ceinture/âge non admissible)` : ""}`);
      if (data.skipped?.length) toast.message("Non attribuées (admissibilité)", { description: data.skipped.slice(0, 6).map((s) => `${s.student_name} — ${s.technique} (${s.reason_label})`).join(" · ") + (data.skipped.length > 6 ? ` … +${data.skipped.length - 6}` : "") });
      setCloseTarget(null);
      loadSessions(); loadDashboard();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur lors de la clôture."); }
  };

  const deleteSession = async (id) => {
    if (!window.confirm("Supprimer cette séance ?")) return;
    try { await api.delete(`/ped/sessions/${id}`); toast.success("Séance supprimée."); loadSessions(); }
    catch (e) { toast.error("Erreur suppression."); }
  };

  const tabs = [
    { id: "dashboard", label: "Tableau de bord", icon: Target },
    { id: "plan", label: "Planifier une séance", icon: CalendarPlus },
    { id: "sessions", label: "Séances & clôture", icon: ListChecks },
    { id: "programs", label: "Programmes & disciplines", icon: TrendingUp },
    { id: "videos", label: "Vidéos (IA)", icon: Video },
    { id: "alerts", label: "Alertes", icon: AlertTriangle },
  ];

  useEffect(() => {
    if (tab === "alerts" && !alerts) loadAlerts();
    if (tab === "programs" && programs.length === 0) loadPrograms();
    if (tab === "videos" && vqDiscList.length === 0) loadVqDisciplines();
  }, [tab, alerts, programs.length, loadAlerts, loadPrograms, vqDiscList.length, loadVqDisciplines]);

  return (
    <div className="space-y-6" data-testid="curriculum-coverage-module">
      <PageHeader
        icon={Target}
        title="Couverture du Curriculum"
        subtitle="Planifie tes séances, clôture-les, et suis la progression réelle par groupe, programme et discipline."
      />

      <div className="flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button
            key={t.id}
            data-testid={`tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
              tab === t.id ? "bg-[#D90429] text-white" : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700"
            }`}
          >
            <t.icon size={16} /> {t.label}
          </button>
        ))}
      </div>

      {/* ================= DASHBOARD ================= */}
      {tab === "dashboard" && (
        <div className="space-y-6">
          {loading ? (
            <p className="text-zinc-400">Chargement…</p>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <StatCard label="Groupes suivis" value={dash?.stats?.groups_total ?? 0} icon={Users} tone="amber" />
                <StatCard label="Séances clôturées" value={dash?.stats?.sessions_closed ?? 0} icon={CheckCircle2} tone="emerald" />
                <StatCard label="Séances planifiées" value={dash?.stats?.sessions_planned ?? 0} icon={CalendarPlus} tone="blue" />
              </div>

              <div data-testid="instructor-digest-bar" className="flex flex-wrap items-center justify-between gap-3 p-4 rounded-xl bg-zinc-800 text-white">
                <div className="flex items-center gap-3">
                  <Mail size={20} className="text-amber-400" />
                  <div>
                    <p className="text-sm font-semibold">Digest pédagogique hebdo</p>
                    <p className="text-xs text-zinc-500">Top 5 techniques les moins enseignées par instructeur + reco IA · envoi auto chaque lundi 7h.</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button data-testid="preview-digest-btn" onClick={previewDigest} disabled={digestBusy}
                    className="flex items-center gap-1.5 text-xs font-semibold bg-zinc-700 hover:bg-zinc-600 px-3 py-2 rounded-lg disabled:opacity-60">
                    <Eye size={14} /> Prévisualiser
                  </button>
                  <button data-testid="send-digest-btn" onClick={sendDigest} disabled={digestBusy}
                    className="flex items-center gap-1.5 text-xs font-semibold bg-[#D90429] hover:bg-[#b8031f] px-3 py-2 rounded-lg disabled:opacity-60">
                    <Send size={14} /> {digestBusy ? "…" : "Envoyer maintenant"}
                  </button>
                </div>
              </div>

              <SectionCard title="Couverture par discipline" icon={TrendingUp}>
                <div className="space-y-4">
                  {(dash?.disciplines || []).map((d) => (
                    <div key={d.discipline_slug} data-testid={`disc-cov-${d.discipline_slug}`}>
                      <div className="flex justify-between items-center text-sm mb-1">
                        <span className="font-semibold text-zinc-200">{d.discipline_label}</span>
                        <div className="flex items-center gap-3">
                          <span className="text-zinc-400">{d.coverage_percent}% · {d.groups} groupe(s) · {d.sessions_done} séance(s)</span>
                          <button data-testid={`disc-reco-btn-${d.discipline_slug}`} onClick={() => fetchDiscReco(d.discipline_slug)}
                            disabled={discRecoLoading === d.discipline_slug}
                            className="flex items-center gap-1 text-xs font-semibold text-zinc-100 bg-zinc-800 hover:bg-zinc-700 px-2 py-1 rounded-full disabled:opacity-60">
                            <Sparkles size={12} /> {discRecoLoading === d.discipline_slug ? "…" : "Reco IA"}
                          </button>
                        </div>
                      </div>
                      <CoverageBar pct={d.coverage_percent} />
                      {discReco[d.discipline_slug] && (
                        <div data-testid={`disc-reco-output-${d.discipline_slug}`} className="mt-2 p-3 rounded-lg bg-zinc-800/60 border border-zinc-800 text-xs text-zinc-200 whitespace-pre-wrap leading-relaxed max-h-64 overflow-y-auto">
                          {discReco[d.discipline_slug]}
                        </div>
                      )}
                    </div>
                  ))}
                  {(!dash?.disciplines || dash.disciplines.length === 0) && (
                    <p className="text-sm text-zinc-400">Aucune donnée encore — planifie et clôture une séance.</p>
                  )}
                </div>
              </SectionCard>

              <SectionCard title="Groupes" description="Clique un groupe pour voir les techniques les moins enseignées." icon={Users}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {groups.map((g) => (
                    <button
                      key={g.id}
                      data-testid={`group-card-${g.id}`}
                      onClick={() => openGroupDetail(g.id)}
                      className="text-left p-4 rounded-xl border border-zinc-800 hover:border-[#D90429] hover:shadow-sm transition-all bg-zinc-800"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="font-semibold text-zinc-100">{g.name}</p>
                          <p className="text-xs text-zinc-400">{g.discipline_label} · {g.programme || "—"}</p>
                        </div>
                        <ChevronRight size={18} className="text-zinc-500" />
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex-1"><CoverageBar pct={g.coverage_percent} /></div>
                        <span className="text-sm font-bold text-zinc-200">{g.coverage_percent}%</span>
                      </div>
                      <p className="text-xs text-zinc-500 mt-1">{g.covered_total}/{g.expected_total} techniques · {g.sessions_done} séance(s)</p>
                    </button>
                  ))}
                </div>
              </SectionCard>
            </>
          )}
        </div>
      )}

      {/* ================= PLANIFIER ================= */}
      {tab === "plan" && (
        <SectionCard title="Planifier une séance" description="Choisis le groupe, la date et les techniques prévues du jour." icon={CalendarPlus}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-1">Groupe</label>
              <select
                data-testid="plan-group-select"
                value={planGroupId}
                onChange={(e) => { setPlanGroupId(e.target.value); setPlanSelected([]); }}
                className="w-full border border-zinc-700 rounded-lg px-3 py-2 text-sm"
              >
                <option value="">— Choisir —</option>
                {groups.map((g) => <option key={g.id} value={g.id}>{g.name} ({g.discipline_label})</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-1">Date</label>
              <input data-testid="plan-date-input" type="date" value={planDate} onChange={(e) => setPlanDate(e.target.value)}
                className="w-full border border-zinc-700 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-1">Objectif</label>
              <input data-testid="plan-objective-input" type="text" value={planObjective} onChange={(e) => setPlanObjective(e.target.value)}
                placeholder="Ex: Travail des balayages" className="w-full border border-zinc-700 rounded-lg px-3 py-2 text-sm" />
            </div>
          </div>

          {planGroupId && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Search size={16} className="text-zinc-500" />
                  <input data-testid="tech-search-input" value={techSearch} onChange={(e) => setTechSearch(e.target.value)}
                    placeholder="Rechercher une technique…" className="flex-1 border border-zinc-700 rounded-lg px-3 py-2 text-sm" />
                </div>
                <div className="border border-zinc-800 rounded-lg max-h-80 overflow-y-auto divide-y">
                  {techLib.map((t) => {
                    const sel = planSelected.find((x) => x.technique_id === t.id);
                    return (
                      <button key={t.id} data-testid={`tech-lib-${t.id}`} onClick={() => togglePlanTech(t)}
                        className={`w-full text-left px-3 py-2 text-sm flex justify-between items-center hover:bg-zinc-800/60 ${sel ? "bg-[#D90429]/20" : ""}`}>
                        <span><span className="font-medium">{t.name}</span> <span className="text-xs text-zinc-500">· {t.category} · {t.belt}</span></span>
                        {sel ? <CheckCircle2 size={16} className="text-[#D90429]" /> : <Plus size={16} className="text-zinc-500" />}
                      </button>
                    );
                  })}
                  {techLib.length === 0 && <p className="px-3 py-4 text-sm text-zinc-500">Aucune technique.</p>}
                </div>
              </div>
              <div>
                <p className="text-sm font-semibold text-zinc-300 mb-2">Techniques du jour ({planSelected.length})</p>
                <div className="border border-zinc-800 rounded-lg max-h-80 overflow-y-auto divide-y">
                  {planSelected.map((p) => (
                    <div key={p.technique_id} className="px-3 py-2 text-sm flex justify-between items-center">
                      <span>{p.name}</span>
                      <button data-testid={`plan-remove-${p.technique_id}`} onClick={() => togglePlanTech({ id: p.technique_id })} className="text-zinc-500 hover:text-[#D90429]"><X size={15} /></button>
                    </div>
                  ))}
                  {planSelected.length === 0 && <p className="px-3 py-4 text-sm text-zinc-500">Sélectionne des techniques à gauche.</p>}
                </div>
                <button data-testid="save-plan-btn" onClick={savePlan}
                  className="mt-4 w-full bg-[#D90429] text-white font-semibold py-2.5 rounded-lg hover:bg-[#b8031f] flex items-center justify-center gap-2">
                  <Save size={16} /> Enregistrer la séance
                </button>
              </div>
            </div>
          )}
        </SectionCard>
      )}

      {/* ================= SÉANCES ================= */}
      {tab === "sessions" && (
        <SectionCard title="Séances" description="Clôture une séance planifiée pour mettre à jour automatiquement les carnets." icon={ListChecks}>
          {sessions.length === 0 ? (
            <EmptyState icon={CalendarPlus} title="Aucune séance" description="Planifie ta première séance dans l'onglet « Planifier une séance »." />
          ) : (
            <div className="space-y-2">
              {sessions.map((s) => (
                <div key={s.id} data-testid={`session-row-${s.id}`}
                  className="flex flex-wrap items-center justify-between gap-3 p-3 rounded-lg border border-zinc-800 bg-zinc-800">
                  <div>
                    <p className="font-semibold text-zinc-100">{s.group_name} <span className="text-xs text-zinc-500">· {s.discipline_label}</span></p>
                    <p className="text-xs text-zinc-400">{s.date} · {s.objective || "Sans objectif"} · {(s.planned_techniques || []).length} technique(s) prévue(s)</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                      s.status === "planned" ? "bg-blue-100 text-blue-700" :
                      s.status === "completed" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                      {s.status === "planned" ? "Planifiée" : s.status === "completed" ? "Clôturée" : "Partielle"}
                    </span>
                    {s.status === "planned" && (
                      <button data-testid={`close-session-${s.id}`} onClick={() => startClose(s)}
                        className="text-sm font-semibold bg-[#D90429] text-white px-3 py-1.5 rounded-lg hover:bg-[#b8031f]">Clôturer</button>
                    )}
                    <button data-testid={`delete-session-${s.id}`} onClick={() => deleteSession(s.id)} className="text-zinc-500 hover:text-[#D90429]"><Trash2 size={16} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </SectionCard>
      )}

      {/* ================= PROGRAMMES & DISCIPLINES ================= */}
      {tab === "programs" && (
        <SectionCard title="Couverture par programme & discipline" description="Vue agrégée — repère les programmes les moins couverts." icon={TrendingUp}>
          {programs.length === 0 ? (
            <p className="text-sm text-zinc-400">Chargement…</p>
          ) : (
            <div className="space-y-3">
              {programs.map((p, i) => (
                <div key={i} data-testid={`program-row-${i}`} className="p-3 rounded-lg border border-zinc-800">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="font-semibold text-zinc-200">{p.discipline_label} <span className="text-zinc-500">· {p.programme}</span></span>
                    <span className="text-zinc-400">{p.coverage_percent}% · {p.groups} groupe(s) · {p.sessions_done} séance(s)</span>
                  </div>
                  <CoverageBar pct={p.coverage_percent} />
                </div>
              ))}
            </div>
          )}
        </SectionCard>
      )}

      {/* ================= VIDÉOS (IA) — file d'auto-liaison ================= */}
      {tab === "videos" && (
        <div className="space-y-6" data-testid="video-queue-tab">
          <SectionCard title="Auto-lier les vidéos manquantes" description="Choisis une discipline, puis colle l'URL Bunny de la vidéo pour chaque technique." icon={Video}>
            <div className="flex flex-wrap gap-2 mb-4">
              {vqDiscList.map((d) => (
                <button key={d.slug} data-testid={`vq-disc-${d.slug}`} onClick={() => loadVideoQueue(d.slug)}
                  className={`px-3 py-2 rounded-lg text-sm font-semibold border transition-colors ${
                    vqDisc === d.slug ? "bg-[#D90429] text-white border-[#D90429]" : "bg-zinc-800 text-zinc-300 border-zinc-800 hover:bg-zinc-800/60"
                  }`}>
                  {d.name} <span className="text-xs opacity-70">· {d.techniques_total}</span>
                </button>
              ))}
            </div>

            {!vqDisc && <EmptyState icon={Video} title="Choisis une discipline" description="Sélectionne une discipline ci-dessus pour démarrer la file de validation." />}

            {vqDisc && vqList.length === 0 && !vqLoading && (
              <div className="p-6 text-center text-sm text-emerald-700 bg-emerald-500/15 border border-emerald-100 rounded-lg" data-testid="vq-done">
                🎉 Toutes les techniques de cette discipline ont déjà une vidéo liée !
              </div>
            )}

            {vqDisc && vqList.length > 0 && (
              <div className="rounded-xl border border-zinc-800 overflow-hidden" data-testid="vq-card">
                <div className="flex items-center justify-between px-4 py-2 bg-zinc-800 text-white text-xs">
                  <span>Technique <strong data-testid="vq-progress">{Math.min(vqIndex + 1, vqList.length)}</strong> / {vqList.length} à lier</span>
                  <button data-testid="vq-skip-btn" onClick={vqSkip} className="flex items-center gap-1 font-semibold text-zinc-500 hover:text-white">
                    Passer <ChevronRight size={14} />
                  </button>
                </div>
                <div className="p-5">
                  <p className="text-lg font-bold text-zinc-100" data-testid="vq-tech-name">{vqList[vqIndex]?.name}</p>
                  <p className="text-xs text-zinc-400 mb-4">{vqList[vqIndex]?.belt} · {vqList[vqIndex]?.category}</p>

                  {vqLoading && <p className="text-sm text-violet-600">Génération de la suggestion IA…</p>}
                  {!vqLoading && vqSuggest && (
                    <div className="space-y-3">
                      {vqSuggest.reason && <p className="text-xs text-zinc-400 italic">{vqSuggest.reason}</p>}
                      {vqSuggest.best_candidate && (
                        <div className="flex items-center gap-3 p-2 rounded-lg bg-zinc-800 border border-zinc-800">
                          {vqSuggest.best_candidate.thumbnail_url && (
                            <img src={vqSuggest.best_candidate.thumbnail_url} alt="" className="w-20 h-12 object-cover rounded" />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-zinc-200 truncate">{vqSuggest.best_candidate.title}</p>
                            <p className="text-[10px] text-emerald-600 font-semibold">Meilleur candidat existant</p>
                          </div>
                          <button data-testid="vq-validate-btn" onClick={() => vqLink(vqSuggest.best_candidate.url)}
                            className="flex items-center gap-1 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 px-3 py-1.5 rounded">
                            <Link2 size={13} /> Valider
                          </button>
                        </div>
                      )}
                      <div className="flex items-center gap-2 pt-1">
                        <input value={vqManual} onChange={(e) => setVqManual(e.target.value)} data-testid="vq-manual-input"
                          placeholder="Colle l'URL Bunny de la vidéo" className="flex-1 text-sm px-2.5 py-1.5 border border-zinc-700 rounded" />
                        <button data-testid="vq-manual-link-btn" onClick={() => vqLink(vqManual.trim())} disabled={!vqManual.trim()}
                          className="flex items-center gap-1 text-xs font-semibold text-white bg-zinc-700 hover:bg-zinc-600 disabled:opacity-50 px-3 py-1.5 rounded">
                          <Link2 size={13} /> Lier
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </SectionCard>
        </div>
      )}


      {/* ================= ALERTES ================= */}
      {tab === "alerts" && (
        <div className="space-y-6">
          <SectionCard title="Techniques oubliées" description="Jamais enseignées dans tes séances clôturées." icon={AlertTriangle}>
            {!alerts ? <p className="text-sm text-zinc-400">Chargement…</p> : (
              alerts.forgotten_techniques.length === 0 ? <p className="text-sm text-zinc-400">Aucune lacune détectée 🎉</p> : (
                <div className="space-y-2">
                  {alerts.forgotten_techniques.map((f) => (
                    <div key={f.discipline_slug} data-testid={`alert-forgotten-${f.discipline_slug}`} className="p-3 rounded-lg border border-amber-200 bg-amber-50">
                      <p className="font-semibold text-sm text-zinc-200">{f.discipline_label} — {f.never_taught_count} technique(s) jamais enseignée(s)</p>
                      <p className="text-xs text-zinc-400 mt-1">Ex : {f.examples.join(", ")}…</p>
                    </div>
                  ))}
                </div>
              )
            )}
          </SectionCard>
          <SectionCard title="Élèves en retard" description="Progression moyenne sous 30% — à encourager / cibler." icon={Users}>
            {!alerts ? <p className="text-sm text-zinc-400">Chargement…</p> : (
              alerts.students_behind.length === 0 ? <p className="text-sm text-zinc-400">Aucun élève en retard.</p> : (
                <div className="divide-y">
                  {alerts.students_behind.map((s) => (
                    <div key={s.profile_id} data-testid={`alert-student-${s.profile_id}`} className="flex justify-between items-center py-2 text-sm">
                      <span>{s.student_name} <span className="text-xs text-zinc-500">· {s.discipline_label}</span></span>
                      <span className="font-bold text-[#D90429]">{s.avg_percent}%</span>
                    </div>
                  ))}
                </div>
              )
            )}
          </SectionCard>
        </div>
      )}

      {/* ================= MODAL CLÔTURE ================= */}
      {closeTarget && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="close-modal">
          <div className="bg-zinc-800 rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-zinc-800 border-b px-6 py-4 flex justify-between items-center">
              <div>
                <h3 className="font-bold text-lg text-zinc-100">Clôturer — {closeTarget.group_name}</h3>
                <p className="text-xs text-zinc-400">{closeTarget.date} · {closeTarget.discipline_label}</p>
              </div>
              <button data-testid="close-modal-x" onClick={() => setCloseTarget(null)} className="text-zinc-500 hover:text-zinc-300"><X size={20} /></button>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p className="font-semibold text-sm text-zinc-300 mb-2 flex items-center gap-1"><BookOpen size={15} /> Techniques enseignées</p>
                <div className="space-y-2">
                  {Object.entries(taughtMap).map(([tid, v]) => (
                    <div key={tid} className="p-2 rounded-lg border border-zinc-800">
                    <div className="flex items-center gap-2">
                      <input data-testid={`taught-check-${tid}`} type="checkbox" checked={v.checked}
                        onChange={(e) => setTaughtMap((m) => ({ ...m, [tid]: { ...m[tid], checked: e.target.checked } }))} />
                      <span className="flex-1 text-sm">{v.name}</span>
                      <input data-testid={`taught-reps-${tid}`} type="number" min={0} value={v.reps}
                        onChange={(e) => setTaughtMap((m) => ({ ...m, [tid]: { ...m[tid], reps: e.target.value } }))}
                        className="w-16 border border-zinc-700 rounded px-2 py-1 text-sm" placeholder="reps" />
                      <button type="button" onClick={() => setTaughtMap((m) => ({ ...m, [tid]: { ...m[tid], showExcl: !m[tid].showExcl } }))} className="text-[10px] border border-zinc-700 px-1.5 py-0.5 rounded text-zinc-400" data-testid={`taught-participants-${tid}`}>{Object.values(v.excluded || {}).filter(Boolean).length ? `${Object.values(v.excluded).filter(Boolean).length} retiré(s)` : "Participants"}</button>
                    </div>
                    {v.showExcl && <div className="mt-2 flex flex-wrap gap-1">{profiles.filter((p) => presentMap[p.id]).map((p) => <button key={p.id} type="button" onClick={() => setTaughtMap((m) => ({ ...m, [tid]: { ...m[tid], excluded: { ...(m[tid].excluded || {}), [p.id]: !(m[tid].excluded || {})[p.id] } } }))} className={`text-[11px] px-2 py-0.5 rounded border ${(v.excluded || {})[p.id] ? "border-zinc-700 text-zinc-500 line-through" : "border-emerald-500/50 text-emerald-200"}`} data-testid={`taught-part-${tid}-${p.id}`}>{p.student_name}</button>)}<span className="text-[10px] text-zinc-500 w-full">Clique un élève pour le retirer de cette technique (il n'a pas fait l'exercice).</span></div>}
                    </div>
                  ))}
                  {Object.keys(taughtMap).length === 0 && <p className="text-sm text-zinc-500">Aucune technique prévue.</p>}
                </div>
              </div>
              <div>
                <p className="font-semibold text-sm text-zinc-300 mb-2 flex items-center gap-1"><Users size={15} /> Présences ({profiles.filter((p) => presentMap[p.id]).length})</p>
                <div className="border border-zinc-800 rounded-lg max-h-72 overflow-y-auto divide-y">
                  {profiles.map((p) => (
                    <button key={p.id} data-testid={`present-${p.id}`}
                      onClick={() => setPresentMap((m) => ({ ...m, [p.id]: !m[p.id] }))}
                      className={`w-full text-left px-3 py-2 text-sm flex justify-between items-center hover:bg-zinc-800/60 ${presentMap[p.id] ? "bg-emerald-500/15" : ""}`}>
                      <span>{p.student_name} <span className="text-xs text-zinc-500">· {p.current_belt}</span></span>
                      {presentMap[p.id] && <CheckCircle2 size={16} className="text-emerald-600" />}
                    </button>
                  ))}
                  {profiles.length === 0 && <p className="px-3 py-4 text-sm text-zinc-500">Aucun élève pour cette discipline.</p>}
                </div>
              </div>
            </div>
            <div className="sticky bottom-0 bg-zinc-800 border-t px-6 py-4 flex justify-end gap-2">
              <button onClick={() => setCloseTarget(null)} className="px-4 py-2 text-sm font-semibold text-zinc-400 hover:bg-zinc-800 rounded-lg">Annuler</button>
              <button data-testid="submit-close-btn" onClick={submitClose}
                className="px-5 py-2 text-sm font-semibold bg-[#D90429] text-white rounded-lg hover:bg-[#b8031f] flex items-center gap-2">
                <CheckCircle2 size={16} /> Clôturer & synchroniser les carnets
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ================= DRAWER DÉTAIL GROUPE ================= */}
      {detail && (
        <div className="fixed inset-0 bg-black/50 z-50 flex justify-end" data-testid="group-detail-drawer" onClick={() => setDetail(null)}>
          <div className="bg-zinc-800 w-full max-w-lg h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-zinc-800 border-b px-6 py-4 flex justify-between items-center">
              <div>
                <h3 className="font-bold text-lg text-zinc-100">{detail.group.name}</h3>
                <p className="text-xs text-zinc-400">{detail.group.discipline_label} · couverture {detail.coverage_percent}% ({detail.covered_total}/{detail.expected_total})</p>
              </div>
              <button data-testid="drawer-close" onClick={() => setDetail(null)} className="text-zinc-500 hover:text-zinc-300"><X size={20} /></button>
            </div>
            <div className="p-6">
              {videoEng && videoEng.videos_total > 0 && (
                <div className="mb-4 p-3 rounded-lg bg-blue-50 border border-blue-100 flex items-center gap-2 text-sm">
                  <Video size={16} className="text-blue-500" />
                  <span className="text-zinc-300">Engagement vidéo : <strong>{videoEng.engagement_percent}%</strong> ({videoEng.viewed_total}/{videoEng.videos_total} techniques avec vidéo consultées)</span>
                </div>
              )}

              <div className="mb-5">
                <button data-testid="reco-ia-btn" onClick={() => fetchReco(detail.group.id)} disabled={recoLoading}
                  className="w-full flex items-center justify-center gap-2 bg-zinc-800 text-white font-semibold py-2.5 rounded-lg hover:bg-zinc-700 disabled:opacity-60">
                  <Sparkles size={16} /> {recoLoading ? "Génération en cours…" : "Reco IA — quoi enseigner cette semaine"}
                </button>
                {reco && (
                  <div data-testid="reco-ia-output" className="mt-3 p-4 rounded-lg bg-zinc-800/60 border border-zinc-800 text-sm text-zinc-200 whitespace-pre-wrap leading-relaxed max-h-80 overflow-y-auto">
                    {reco}
                  </div>
                )}
              </div>

              <p className="text-sm font-semibold text-zinc-300 mb-3 flex items-center gap-1"><AlertTriangle size={15} className="text-amber-500" /> Techniques les moins enseignées (à prioriser)</p>
              <div className="space-y-1">
                {detail.techniques.slice(0, 40).map((t) => (
                  <div key={t.technique_id} className="border-b border-zinc-800 py-1.5" data-testid={`tech-row-${t.technique_id}`}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-1.5">
                        {t.covered ? <CheckCircle2 size={14} className="text-emerald-500" /> : <span className="w-3.5 h-3.5 rounded-full border border-zinc-700 inline-block" />}
                        {t.name} <span className="text-xs text-zinc-500">· {t.category}</span>
                        {t.has_video && <Video size={12} className="text-blue-400" />}
                      </span>
                      <span className="flex items-center gap-2">
                        <span className="text-xs text-zinc-400">{t.taught_count}× {t.last_taught ? `· ${t.last_taught}` : ""}</span>
                        <button
                          data-testid={`video-ia-btn-${t.technique_id}`}
                          onClick={() => (vidTech === t.technique_id ? setVidTech(null) : openVideoSuggest(t.technique_id))}
                          title={t.has_video ? "Changer la vidéo (IA)" : "Suggérer une vidéo (IA)"}
                          className="flex items-center gap-1 text-[11px] font-semibold text-violet-700 hover:text-violet-900 border border-violet-200 hover:border-violet-300 bg-violet-50 px-2 py-0.5 rounded">
                          <Sparkles size={11} /> {t.has_video ? "Vidéo" : "Lier vidéo"}
                        </button>
                      </span>
                    </div>
                    {vidTech === t.technique_id && (
                      <div className="mt-2 p-3 rounded-lg bg-violet-50/60 border border-violet-100 text-sm" data-testid={`video-ia-panel-${t.technique_id}`}>
                        {vidLoading && <p className="text-zinc-400 text-xs">Génération de la suggestion IA…</p>}
                        {!vidLoading && vidSuggest && (
                          <div className="space-y-2">
                            {vidSuggest.reason && <p className="text-xs text-zinc-400 italic">{vidSuggest.reason}</p>}
                            {vidSuggest.best_candidate && (
                              <div className="flex items-center gap-2 p-2 rounded-md bg-zinc-800 border border-zinc-800">
                                {vidSuggest.best_candidate.thumbnail_url && (
                                  <img src={vidSuggest.best_candidate.thumbnail_url} alt="" className="w-16 h-10 object-cover rounded" />
                                )}
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs font-semibold text-zinc-200 truncate">{vidSuggest.best_candidate.title}</p>
                                  <p className="text-[10px] text-emerald-600 font-semibold">Meilleur candidat existant</p>
                                </div>
                                <button data-testid="video-ia-validate-btn"
                                  onClick={() => linkVideo(t.technique_id, vidSuggest.best_candidate.url)}
                                  className="flex items-center gap-1 text-[11px] font-bold text-white bg-emerald-600 hover:bg-emerald-700 px-2.5 py-1 rounded">
                                  <Link2 size={12} /> Valider
                                </button>
                              </div>
                            )}
                            <div className="flex items-center gap-2 pt-1">
                              <input value={manualUrl} onChange={(e) => setManualUrl(e.target.value)}
                                data-testid="video-ia-manual-input"
                                placeholder="Colle l'URL Bunny de la vidéo"
                                className="flex-1 text-xs px-2 py-1 border border-zinc-700 rounded" />
                              <button data-testid="video-ia-manual-link-btn"
                                onClick={() => linkVideo(t.technique_id, manualUrl.trim())}
                                disabled={!manualUrl.trim()}
                                className="flex items-center gap-1 text-[11px] font-semibold text-white bg-zinc-700 hover:bg-zinc-600 disabled:opacity-50 px-2.5 py-1 rounded">
                                <Link2 size={12} /> Lier
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
